import { useState } from 'react';
import { IconListFilter } from '@iwoca/icons';
import { Column } from '@tanstack/react-table';
import clsx from 'clsx';
import { useAmplitude } from 'hooks/useAmplitude';
import { TApplication } from 'lendingApi/applications.types';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { useApplicationsTableState } from '../ApplicationsTableState.context';
import { DateRangeColumnFilter } from './DateRangeColumnFilter/DateRangeColumnFilter';
import styles from './Filter.module.css';
import { NumberRangeColumnFilter } from './NumberRangeColumnFilter/NumberRangeColumnFilter';
import { SearchColumnFilter } from './SearchColumnFilter/SearchColumnFilter';
import { SelectColumnFilter } from './SelectColumnFilter/SelectColumnFilter';

type TProps = {
  column: Column<TApplication, unknown>;
};

export const Filter = ({ column }: TProps) => {
  const { table } = useApplicationsTableState();
  const { trackEvent } = useAmplitude();
  const [isOpen, setIsOpen] = useState(false);
  const { filterVariant } = column.columnDef.meta ?? {};
  const columns = table.getVisibleLeafColumns();
  const isFilterApplied = column.getIsFiltered();
  const columnIndex = columns.findIndex((col) => col.id === column.id);

  const alignFilterRight = columnIndex < columns.length - 2;

  const handleClick = () => {
    if (!isOpen) {
      trackEvent(AMPLITUDE_EVENTS.tableFilterOpened, {
        'Filter name': column.columnDef.header,
      });
    }
    setIsOpen((prev) => !prev);
  };

  if (!filterVariant) {
    return null;
  }

  const renderFilterInput = () => {
    switch (filterVariant) {
      case 'text':
        return (
          <SearchColumnFilter
            column={column}
            onClose={() => setIsOpen(false)}
          />
        );
      case 'dateRange':
        return (
          <DateRangeColumnFilter
            column={column}
            onClose={() => setIsOpen(false)}
          />
        );
      case 'select':
        return (
          <SelectColumnFilter
            column={column}
            onClose={() => setIsOpen(false)}
          />
        );
      case 'numberRange':
        return (
          <NumberRangeColumnFilter
            column={column}
            onClose={() => setIsOpen(false)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.container}>
      <button
        className={styles.filterToggle}
        data-testid={`${column.columnDef.id}-filter-toggle`}
        onClick={handleClick}
      >
        <IconListFilter height={16} width={16} />
        {isFilterApplied && <div className={styles.indicator} />}
      </button>
      {isOpen && (
        <>
          <div
            className={clsx(styles.filterCard, {
              [styles.rightAlignFilter]: alignFilterRight,
            })}
          >
            {renderFilterInput()}
          </div>
          <button
            className={styles.filterBackdrop}
            onClick={() => setIsOpen(false)}
          ></button>
        </>
      )}
    </div>
  );
};
